import type { ComponentProjectMetricsResponse, DashboardMetric } from '../../../../types/dashboard';
import { getConfiguredSubawardeeIdsForComponentProject } from '../../../../config/componentProjects';
import { MAX_QUARTERS_CHART } from '../../constants';
import { readPointFieldGoal } from '../../chartGoalUtils';
import { parseQuarter } from '../../metricSeriesUtils';

export type SafeTComponentProjectPoint = {
  quarter: string;
  clientsEngaged: number;
  clientsAcquired: number;
  tulsaEmployment: number;
  facilityUtilization: number;
  revenue: number;
  revenueBySubawardee?: Record<string, number>;
  clientsEngagedBySubawardee?: Record<string, number>;
  clientsAcquiredBySubawardee?: Record<string, number>;
  goalTargets?: {
    clientsEngaged?: number;
    clientsAcquired?: number;
    tulsaEmployment?: number;
    facilityUtilization?: number;
    revenue?: number;
  };
};

export type SafeTComponentProjectData = {
  series: SafeTComponentProjectPoint[];
  allTime: {
    clientsEngaged: number;
    clientsAcquired: number;
    tulsaEmployment: number;
    facilityUtilization: number;
    revenue: number;
  };
  fieldsSeen: Set<keyof SafeTComponentProjectPoint>;
  /** Subawardee ids present in per-subawardee revenue breakdown, when available. */
  revenueSubawardeeIds: string[];
  /** Subawardee ids present in per-subawardee client acquisition breakdown, when available. */
  clientAcquisitionSubawardeeIds: string[];
};

const CLIENT_ENGAGED_KEYS = ['clients_engaged', 'clientsEngaged', 'engaged'];
const CLIENT_ACQUIRED_KEYS = [
  'new_clients_acquired',
  'clients_acquired',
  'clientsAcquired',
  'acquired',
];
const TULSA_EMPLOYMENT_KEYS = [
  'tulsa_employment',
  'tulsaEmployment',
  'ftes_employed_in_tulsa',
  'ftesEmployedInTulsa',
];
const FACILITY_UTILIZATION_KEYS = ['facility_utilization', 'facilityUtilization'];
const REVENUE_KEYS = ['revenue', 'revenue_all_clients', 'revenueAllClients', 'organization_revenue', 'organizationRevenue'];

const SERIES_META_KEYS = new Set(['quarter', 'qoq', 'yoy', 'goal', 'goalStatus']);

function slugifySubawardee(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function resolveSubawardeeIdFromKey(key: string, subawardeeIds: string[]): string | null {
  const normalizedKey = key.trim().toLowerCase();
  const slugKey = slugifySubawardee(key);

  for (const subawardeeId of subawardeeIds) {
    if (subawardeeId.toLowerCase() === normalizedKey) return subawardeeId;
    if (slugifySubawardee(subawardeeId) === slugKey) return subawardeeId;
  }

  return null;
}

function readNullableNumericField(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
}

function resolveBreakdownSubawardeeId(
  rawKey: string,
  entry: { groupId?: string; groupName?: string },
  subawardeeIds: string[]
): string {
  return (
    resolveSubawardeeIdFromKey(rawKey, subawardeeIds) ??
    resolveSubawardeeIdFromKey(entry.groupName ?? '', subawardeeIds) ??
    resolveSubawardeeIdFromKey(entry.groupId ?? '', subawardeeIds) ??
    rawKey
  );
}

function readSubawardeeFieldBreakdown(
  metric: DashboardMetric,
  subawardeeIds: string[],
  fieldKeys: string[]
): Map<string, Record<string, number>> | null {
  const bySubawardee = metric.breakdowns?.bySubawardee;
  if (!bySubawardee) return null;

  const byQuarter = new Map<string, Record<string, number>>();

  for (const [rawKey, entry] of Object.entries(bySubawardee)) {
    const subawardeeId = resolveBreakdownSubawardeeId(rawKey, entry, subawardeeIds);

    for (const point of entry.series ?? []) {
      const quarter = String(point.quarter ?? '');
      if (!quarter) continue;

      let value = 0;
      for (const fieldKey of fieldKeys) {
        if (fieldKey in point) {
          value = readNullableNumericField(point[fieldKey]);
          break;
        }
      }

      const merged = byQuarter.get(quarter) ?? {};
      merged[subawardeeId] = value;
      byQuarter.set(quarter, merged);
    }
  }

  return byQuarter.size > 0 ? byQuarter : null;
}

function readRevenueSubawardeeBreakdown(
  metric: DashboardMetric,
  subawardeeIds: string[]
): Map<string, Record<string, number>> | null {
  return readSubawardeeFieldBreakdown(metric, subawardeeIds, REVENUE_KEYS);
}

function collectActiveSubawardeeIds(
  byQuarter: Map<string, Record<string, number>>,
  configuredSubawardeeIds: string[]
): string[] {
  const active = new Set<string>();

  for (const values of Array.from(byQuarter.values())) {
    for (const [subawardeeId, value] of Object.entries(values)) {
      if (typeof value === 'number' && value > 0) {
        active.add(subawardeeId);
      }
    }
  }

  return configuredSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
}

function collectActiveRevenueSubawardeeIds(
  byQuarter: Map<string, Record<string, number>>,
  configuredSubawardeeIds: string[]
): string[] {
  return collectActiveSubawardeeIds(byQuarter, configuredSubawardeeIds);
}

function collectActiveClientAcquisitionSubawardeeIds(
  engagedByQuarter: Map<string, Record<string, number>>,
  acquiredByQuarter: Map<string, Record<string, number>>,
  configuredSubawardeeIds: string[]
): string[] {
  const active = new Set<string>([
    ...collectActiveSubawardeeIds(engagedByQuarter, configuredSubawardeeIds),
    ...collectActiveSubawardeeIds(acquiredByQuarter, configuredSubawardeeIds),
  ]);
  return configuredSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
}

function quarterSortKey(label: string): number {
  const parsed = parseQuarter(label);
  if (!parsed) return 0;
  return parsed.year * 10 + parsed.quarter;
}

function readNumericField(point: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const value = point[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function readAllTimeField(allTime: Record<string, unknown> | null | undefined, keys: string[]): number {
  if (!allTime) return 0;
  return readNumericField(allTime, keys);
}

function inferSafeTFieldFromMetricId(metricId: string): keyof SafeTComponentProjectPoint | null {
  const id = metricId.toLowerCase();
  if (
    id.includes('tulsa_employment') ||
    id.includes('tulsaemployment') ||
    (id.includes('tulsa') && (id.includes('employ') || id.includes('fte')))
  ) {
    return 'tulsaEmployment';
  }
  if (id.includes('facility_utilization') || (id.includes('facility') && id.includes('util'))) {
    return 'facilityUtilization';
  }
  if (id.includes('clients_acquired') || id.includes('new_clients_acquired') || (id.includes('client') && id.includes('acquir'))) {
    return 'clientsAcquired';
  }
  if (id.includes('clients_engaged') || (id.includes('client') && id.includes('engag'))) {
    return 'clientsEngaged';
  }
  return null;
}

function markFieldsSeenFromRaw(
  raw: Record<string, unknown>,
  fieldsSeen: Set<keyof SafeTComponentProjectPoint>
): void {
  if (CLIENT_ENGAGED_KEYS.some((key) => key in raw)) fieldsSeen.add('clientsEngaged');
  if (CLIENT_ACQUIRED_KEYS.some((key) => key in raw)) fieldsSeen.add('clientsAcquired');
  if (TULSA_EMPLOYMENT_KEYS.some((key) => key in raw)) fieldsSeen.add('tulsaEmployment');
  if (FACILITY_UTILIZATION_KEYS.some((key) => key in raw)) fieldsSeen.add('facilityUtilization');
  if (REVENUE_KEYS.some((key) => key in raw)) fieldsSeen.add('revenue');

  for (const [key, value] of Object.entries(raw)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const normalized = key.toLowerCase();
    if (normalized.includes('tulsa') && (normalized.includes('employ') || normalized.includes('fte'))) {
      raw.tulsa_employment ??= value;
      fieldsSeen.add('tulsaEmployment');
    }
  }
}

function mergePointGoals(
  merged: Record<string, unknown>,
  point: Record<string, unknown>
): void {
  if (!point.goal || typeof point.goal !== 'object') return;
  const existing =
    merged.goal && typeof merged.goal === 'object'
      ? (merged.goal as Record<string, unknown>)
      : {};
  merged.goal = { ...existing, ...(point.goal as Record<string, unknown>) };
}

function applyMetricLevelGoals(
  merged: Record<string, unknown>,
  quarter: string,
  metric: DashboardMetric
): void {
  const activeGoals = (metric as DashboardMetric & { goals?: { active?: Array<{
    grain?: string;
    periodKey?: string;
    targets?: Record<string, { operator?: string; value?: number }>;
  }> } }).goals?.active;
  if (!Array.isArray(activeGoals) || activeGoals.length === 0) return;

  const meta = parseQuarter(quarter);
  if (!meta) return;

  const existingGoal =
    merged.goal && typeof merged.goal === 'object'
      ? ({ ...(merged.goal as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  for (const goal of activeGoals) {
    const isAnnual = goal.grain === 'annual' && goal.periodKey === String(meta.year);
    const isQuarterly =
      goal.grain === 'quarter' && goal.periodKey === `${meta.year}-Q${meta.quarter}`;
    if (!isAnnual && !isQuarterly) continue;

    for (const [fieldName, target] of Object.entries(goal.targets ?? {})) {
      if (existingGoal[fieldName]) continue;
      if (typeof target.value === 'number' && Number.isFinite(target.value)) {
        existingGoal[fieldName] = {
          operator: target.operator,
          target: target.value,
        };
      }
    }
  }

  if (Object.keys(existingGoal).length > 0) {
    merged.goal = existingGoal;
  }
}

function toSafeTPoint(quarter: string, raw: Record<string, unknown>): SafeTComponentProjectPoint {
  const goalTargets = {
    clientsEngaged:
      readPointFieldGoal(raw, CLIENT_ENGAGED_KEYS) ??
      undefined,
    clientsAcquired:
      readPointFieldGoal(raw, CLIENT_ACQUIRED_KEYS) ??
      undefined,
    tulsaEmployment:
      readPointFieldGoal(raw, TULSA_EMPLOYMENT_KEYS) ??
      undefined,
    facilityUtilization:
      readPointFieldGoal(raw, FACILITY_UTILIZATION_KEYS) ??
      undefined,
    revenue:
      readPointFieldGoal(raw, REVENUE_KEYS) ??
      undefined,
  };
  const hasGoals = Object.values(goalTargets).some((value) => value !== undefined);
  const revenueBySubawardee = raw.revenueBySubawardee as Record<string, number> | undefined;
  const clientsEngagedBySubawardee = raw.clientsEngagedBySubawardee as
    | Record<string, number>
    | undefined;
  const clientsAcquiredBySubawardee = raw.clientsAcquiredBySubawardee as
    | Record<string, number>
    | undefined;

  return {
    quarter,
    clientsEngaged: readNumericField(raw, CLIENT_ENGAGED_KEYS),
    clientsAcquired: readNumericField(raw, CLIENT_ACQUIRED_KEYS),
    tulsaEmployment: readNumericField(raw, TULSA_EMPLOYMENT_KEYS),
    facilityUtilization: readNumericField(raw, FACILITY_UTILIZATION_KEYS),
    revenue: readNumericField(raw, REVENUE_KEYS),
    ...(revenueBySubawardee && Object.keys(revenueBySubawardee).length > 0
      ? { revenueBySubawardee }
      : {}),
    ...(clientsEngagedBySubawardee && Object.keys(clientsEngagedBySubawardee).length > 0
      ? { clientsEngagedBySubawardee }
      : {}),
    ...(clientsAcquiredBySubawardee && Object.keys(clientsAcquiredBySubawardee).length > 0
      ? { clientsAcquiredBySubawardee }
      : {}),
    ...(hasGoals ? { goalTargets } : {}),
  };
}

export function mapSafeTComponentProjectData(
  data: ComponentProjectMetricsResponse | undefined
): SafeTComponentProjectData | null {
  if (!data?.sections?.length) return null;

  const subawardeeIds = getConfiguredSubawardeeIdsForComponentProject('safe-t');
  const byQuarter = new Map<string, Record<string, unknown>>();
  const allTimeRaw: Record<string, unknown> = {};
  const fieldsSeen = new Set<keyof SafeTComponentProjectPoint>();
  let revenueByQuarter = new Map<string, Record<string, number>>();
  let clientsEngagedByQuarter = new Map<string, Record<string, number>>();
  let clientsAcquiredByQuarter = new Map<string, Record<string, number>>();

  for (const section of data.sections) {
    for (const metric of section.metrics) {
      const revenueBreakdown = readRevenueSubawardeeBreakdown(metric, subawardeeIds);
      if (revenueBreakdown) {
        revenueByQuarter = revenueBreakdown;
      }

      const engagedBreakdown = readSubawardeeFieldBreakdown(
        metric,
        subawardeeIds,
        CLIENT_ENGAGED_KEYS
      );
      if (engagedBreakdown) {
        clientsEngagedByQuarter = engagedBreakdown;
      }

      const acquiredBreakdown = readSubawardeeFieldBreakdown(
        metric,
        subawardeeIds,
        CLIENT_ACQUIRED_KEYS
      );
      if (acquiredBreakdown) {
        clientsAcquiredByQuarter = acquiredBreakdown;
      }

      const inferredField = inferSafeTFieldFromMetricId(metric.metricId);
      if (inferredField) {
        fieldsSeen.add(inferredField);
      }

      for (const point of metric.series) {
        const quarter = String(point.quarter ?? '');
        if (!quarter) continue;

        const merged = byQuarter.get(quarter) ?? { quarter };
        for (const [key, value] of Object.entries(point)) {
          if (!SERIES_META_KEYS.has(key) && typeof value === 'number' && Number.isFinite(value)) {
            merged[key] = value;
          }
        }

        if (inferredField && typeof point.value === 'number' && Number.isFinite(point.value)) {
          const storageKey =
            inferredField === 'tulsaEmployment'
              ? 'tulsa_employment'
              : inferredField === 'clientsEngaged'
                ? 'clients_engaged'
                : inferredField === 'clientsAcquired'
                  ? 'new_clients_acquired'
                  : 'facility_utilization';
          merged[storageKey] = point.value;
        }

        markFieldsSeenFromRaw(merged, fieldsSeen);
        mergePointGoals(merged, point);
        applyMetricLevelGoals(merged, quarter, metric);
        byQuarter.set(quarter, merged);
      }

      if (metric.allTime) {
        for (const [key, value] of Object.entries(metric.allTime)) {
          if (typeof value === 'number' && Number.isFinite(value)) {
            allTimeRaw[key] = value;
          }
        }
        if (inferredField && typeof metric.allTime.value === 'number') {
          const storageKey =
            inferredField === 'tulsaEmployment'
              ? 'tulsa_employment'
              : inferredField === 'clientsEngaged'
                ? 'clients_engaged'
                : inferredField === 'clientsAcquired'
                  ? 'new_clients_acquired'
                  : 'facility_utilization';
          allTimeRaw[storageKey] = metric.allTime.value;
          fieldsSeen.add(inferredField);
        }
      }
    }
  }

  markFieldsSeenFromRaw(allTimeRaw, fieldsSeen);

  const series = Array.from(byQuarter.entries())
    .sort(([a], [b]) => quarterSortKey(a) - quarterSortKey(b))
    .slice(-MAX_QUARTERS_CHART)
    .map(([quarter, raw]) => {
      const revenueForQuarter = revenueByQuarter.get(quarter);
      if (revenueByQuarter.size > 0) {
        raw.revenueBySubawardee = revenueForQuarter ?? {};
      }

      const engagedForQuarter = clientsEngagedByQuarter.get(quarter);
      if (clientsEngagedByQuarter.size > 0) {
        raw.clientsEngagedBySubawardee = engagedForQuarter ?? {};
      }

      const acquiredForQuarter = clientsAcquiredByQuarter.get(quarter);
      if (clientsAcquiredByQuarter.size > 0) {
        raw.clientsAcquiredBySubawardee = acquiredForQuarter ?? {};
      }

      return toSafeTPoint(quarter, raw);
    });

  const revenueSubawardeeIds = collectActiveRevenueSubawardeeIds(revenueByQuarter, subawardeeIds);
  const clientAcquisitionSubawardeeIds = collectActiveClientAcquisitionSubawardeeIds(
    clientsEngagedByQuarter,
    clientsAcquiredByQuarter,
    subawardeeIds
  );

  if (series.length === 0) return null;

  return {
    series,
    allTime: {
      clientsEngaged: readAllTimeField(allTimeRaw, CLIENT_ENGAGED_KEYS),
      clientsAcquired: readAllTimeField(allTimeRaw, CLIENT_ACQUIRED_KEYS),
      tulsaEmployment: readAllTimeField(allTimeRaw, TULSA_EMPLOYMENT_KEYS),
      facilityUtilization: readAllTimeField(allTimeRaw, FACILITY_UTILIZATION_KEYS),
      revenue: readAllTimeField(allTimeRaw, REVENUE_KEYS),
    },
    fieldsSeen,
    revenueSubawardeeIds,
    clientAcquisitionSubawardeeIds,
  };
}

export function hasSafeTClientAcquisitionData(data: SafeTComponentProjectData): boolean {
  return (
    data.fieldsSeen.has('clientsEngaged') ||
    data.fieldsSeen.has('clientsAcquired') ||
    data.series.some((point) => point.clientsEngaged > 0 || point.clientsAcquired > 0)
  );
}

export function hasSafeTTulsaEmploymentData(data: SafeTComponentProjectData): boolean {
  return (
    data.fieldsSeen.has('tulsaEmployment') ||
    data.series.some((point) => point.tulsaEmployment > 0) ||
    data.allTime.tulsaEmployment > 0
  );
}

export function hasSafeTFacilityUtilizationData(data: SafeTComponentProjectData): boolean {
  return (
    data.fieldsSeen.has('facilityUtilization') ||
    data.series.some((point) => point.facilityUtilization > 0)
  );
}

export function hasSafeTRevenueData(data: SafeTComponentProjectData): boolean {
  return (
    data.fieldsSeen.has('revenue') ||
    data.series.some((point) => point.revenue > 0) ||
    data.allTime.revenue > 0 ||
    data.revenueSubawardeeIds.length > 0
  );
}

export function hasSafeTRevenueSubawardeeBreakdown(data: SafeTComponentProjectData): boolean {
  return data.revenueSubawardeeIds.length > 0;
}

export function hasSafeTClientAcquisitionSubawardeeBreakdown(
  data: SafeTComponentProjectData
): boolean {
  return data.clientAcquisitionSubawardeeIds.length > 0;
}
